/* ============================================================
   egy-cx-quiz.js — Energy CX Quiz v2 state machine
   Screens: q1 hero → q2 recognition (multi · verbatim) → q3 timing
          → q4 reflection+education (auto-advance) → q5 EMAIL (mid-funnel)
          → q6 cost (multi · verbatim) → q7 portraits → q8 loading → q9 results
   3-phase progress: q1-q3 story, q4-q6 rhythm, q7-q9 soft landing
   Tracker prefix: egy_cx_quiz_*
   ============================================================ */
(function () {
  "use strict";

  const SCREENS = ["q1", "q2", "q3", "q4", "q5", "q6", "q7", "q8", "q9"];
  const PHASE_OF = { q1:1, q2:1, q3:1, q4:2, q5:2, q6:2, q7:3, q8:3, q9:3 };
  const PCT_OF   = { q1:11.11, q2:22.22, q3:33.33, q4:44.44, q5:55.55, q6:66.66, q7:77.77, q8:88.88, q9:100 };
  const LOADING_DELAY_MS    = 3400;
  const REFLECTION_DELAY_MS = 5500;

  const PRODUCT_TAGS = {
    energy:     { defaultQty: 1 },
    essentials: { defaultQty: 1 },
    sleep:      { defaultQty: 0 },
    beauty:     { defaultQty: 0 }
  };

  const CADENCE = {
    energy:     "7AM DAILY",
    essentials: "9AM DAILY",
    sleep:      "10PM EVERY NIGHT",
    beauty:     "9AM DAILY"
  };

  /* === Energy-led recommendation (verbatim-pill keys) === */
  function computeRecommendation(answers) {
    const q2 = Array.isArray(answers.q2) ? answers.q2 : [];
    const q3 = answers.q3 || "";
    const q6 = Array.isArray(answers.q6) ? answers.q6 : [];

    const severeEnergy =
      q2.includes("redbull-monster") ||
      q2.includes("12hr-shift") ||
      q2.includes("mom-empty") ||
      q2.includes("4-cups") ||
      q2.includes("wired-tired") ||
      q2.includes("dragging-2pm") ||
      q3 === "all-day" ||
      q3 === "mid-afternoon" ||
      q6.includes("crash-2hr") ||
      q6.includes("empty-after-kids");

    const sleepNeeded =
      q2.includes("mornings-torture") ||
      q6.includes("cant-get-up");

    let energyCadence = "7AM DAILY";
    if (q3 === "after-lunch" || q3 === "mid-afternoon") energyCadence = "2PM DAILY";
    if (q3 === "all-day")                                energyCadence = "7AM and 2PM DAILY";
    if (q2.includes("dragging-2pm") && q3 !== "alarm" && q3 !== "all-day") energyCadence = "2PM DAILY";
    if (q2.includes("mornings-torture") && q3 === "alarm") energyCadence = "7AM DAILY";

    const energyQty = severeEnergy ? 2 : 1;
    const sleepQty  = sleepNeeded ? 1 : 0;

    return {
      tiers: {
        energy:     "PRIMARY",
        essentials: "RECOMMENDED",
        sleep:      sleepNeeded ? "RECOMMENDED" : "OPTIONAL",
        beauty:     "OPTIONAL"
      },
      cadenceOverrides: { energy: energyCadence },
      qty: {
        energy:     energyQty,
        essentials: 1,
        sleep:      sleepQty,
        beauty:     0
      },
      cleanEnergyEligible: severeEnergy
    };
  }

  function applyRecommendation(root, rec) {
    Object.keys(rec.tiers).forEach(product => {
      const tagEl = root.querySelector('[data-cx-tag="' + product + '"]');
      const cadence = rec.cadenceOverrides[product] || CADENCE[product];
      if (tagEl) tagEl.textContent = rec.tiers[product] + " · " + cadence;
    });
  }

  /* === Personalize education body based on Q3 timing === */
  function personalizeEducation(root, q3Answer) {
    const body = root.querySelector("[data-cx-edu-body]");
    if (!body) return;
    const map = {
      "alarm":         "When your alarm fires, cortisol's already peaking. Coffee on top is overkill — your body just needs a steady runway. That's what we build.",
      "after-lunch":   "Coffee + sugar pulls you up fast, then crashes you 2 hours later. The post-lunch wall isn't laziness — it's biology.",
      "mid-afternoon": "The 2pm crash is the #1 thing customers tell us they fixed. Coffee spikes glucose, glucose crashes, you wall out. Steady caffeine + L-theanine breaks the loop.",
      "gym":           "Pre-workout overdoses you on caffeine + niacin. Most people need a smaller, smoother lift — enough to push, not enough to wreck the rest of the day.",
      "mid-shift":     "Long shifts kill you because you're caffeine-stacking. Switching to one steady strip flattens the spike-crash you've been riding.",
      "all-day":       "If you're tired all day, you're not just under-caffeinated — you're nutrient-thin. We start with steady energy + a baseline multi, then layer."
    };
    body.textContent = map[q3Answer] || map["mid-afternoon"];
  }

  /* === Discount + variant config === */
  const DISCOUNT_CODES = {
    bundle_2:       "NUBUNDLE2",
    bundle_3:       "NUBUNDLE3",
    wellness_full:  "NUWELL23",
    wellness_three: "NUWELL15",
    clean_energy:   "NUCLEAN15"
  };

  const PRODUCT_CONFIG_DEFAULT = {
    shop_domain:        "nustrips.myshopify.com",
    monthly_selling_plan: "4617470167",
    variants: {
      sleep:      "44052420755671",
      essentials: "46996555563223",
      energy:     "48418858827991",
      beauty:     "44044917375191"
    }
  };

  function init(root) {
    if (!root) return;
    const stage   = root.querySelector("[data-cx-stage]");
    const screens = Array.from(root.querySelectorAll("[data-cx-screen]"));
    const state   = {
      idx: 0,
      answers: {},
      qty: {},
      stepEnteredAt: Date.now(),
      cleanEnergyEligible: false,
      subscribe: true,
      reflectionTimer: null
    };

    Object.keys(PRODUCT_TAGS).forEach(k => state.qty[k] = PRODUCT_TAGS[k].defaultQty);

    const track = (name, payload) => {
      try { if (window.cxTrack) window.cxTrack(name, payload); } catch (e) {}
    };

    track("egy_cx_quiz_started", {});
    track("egy_cx_quiz_step_viewed", { step: "q1" });

    window.addEventListener("beforeunload", function () {
      const reached = window.cxQuizResultsViewed && window.cxQuizResultsViewed();
      if (!reached) {
        track("egy_cx_quiz_abandoned", {
          last_step: SCREENS[state.idx],
          time_in_session_ms: Date.now() - (state.stepEnteredAt || Date.now())
        });
      }
    });

    function go(targetId) {
      const nextIdx = SCREENS.indexOf(targetId);
      if (nextIdx === -1) return;
      const next = screens[nextIdx];
      if (!next) return;
      const cur = screens[state.idx];
      if (next === cur) return;

      if (state.reflectionTimer) {
        clearTimeout(state.reflectionTimer);
        state.reflectionTimer = null;
      }

      screens.forEach(s => {
        if (s === next) return;
        s.classList.remove("is-active");
        s.classList.remove("is-leaving");
      });
      next.classList.remove("is-leaving");
      next.classList.add("is-active");

      state.idx = nextIdx;
      updateProgress();
      updatePhaseRow();

      track("egy_cx_quiz_step_viewed", { step: targetId });
      state.stepEnteredAt = Date.now();

      if (targetId === "q4") {
        // Education interstitial — personalize body by q3 answer, then auto-advance
        personalizeEducation(root, state.answers.q3);
        state.reflectionTimer = setTimeout(() => go("q5"), REFLECTION_DELAY_MS);
      }

      if (targetId === "q5") {
        // Lock recommendation shape so Q5 email screen shows the right % off
        const rec = computeRecommendation(state.answers);
        state.cleanEnergyEligible = !!rec.cleanEnergyEligible;
        Object.keys(rec.qty).forEach(p => { state.qty[p] = rec.qty[p]; });
        lockApprovedPct();
      }

      if (targetId === "q8") {
        track("egy_cx_quiz_loading_viewed", {});
        setTimeout(() => go("q9"), LOADING_DELAY_MS);
      }

      if (targetId === "q9") {
        const rec = computeRecommendation(state.answers);
        state.cleanEnergyEligible = !!rec.cleanEnergyEligible;
        Object.keys(rec.qty).forEach(p => {
          state.qty[p] = rec.qty[p];
          const qtyEl = root.querySelector('[data-cx-qty="' + p + '"]');
          if (qtyEl) qtyEl.textContent = rec.qty[p];
        });
        applyRecommendation(root, rec);
        updateReceipt();
        lockApprovedPct();
        (async () => {
          try {
            const cfg = (window.CX_QUIZ_CONFIG && window.CX_QUIZ_CONFIG.variants) ? window.CX_QUIZ_CONFIG : PRODUCT_CONFIG_DEFAULT;
            const wItems = Object.keys(state.qty).filter(k => state.qty[k] > 0).map(p => ({ product: p, qty: state.qty[p] }));
            const wCodes = computeCartDiscountCodes();
            await warmCart(cfg, wItems, !!state.subscribe, wCodes);
          } catch (e) { console.warn("[egy-cx-quiz] warmCart err:", e); }
        })();
        track("egy_cx_quiz_results_viewed", {
          recommended_stack: Object.keys(state.qty).filter(k => state.qty[k] > 0).join("+"),
          tiers: rec.tiers,
          qty: rec.qty,
          clean_energy_eligible: state.cleanEnergyEligible
        });
      }

      if (stage && stage.scrollIntoView) {
        stage.scrollIntoView({ block: "start", behavior: "instant" });
      }
    }

    function updateProgress() {
      const cur = SCREENS[state.idx];
      const pct = PCT_OF[cur] || 0;
      screens.forEach(s => {
        const fillEl = s.querySelector("[data-cx-progress-fill]");
        if (fillEl) fillEl.style.width = pct + "%";
      });
    }

    function updatePhaseRow() {
      const cur = SCREENS[state.idx];
      const curPhase = PHASE_OF[cur] || 1;
      screens.forEach(s => {
        const row = s.querySelector("[data-cx-phase-row]");
        if (!row) return;
        row.querySelectorAll(".cx-phase").forEach(chip => {
          const p = parseInt(chip.getAttribute("data-cx-phase"), 10);
          chip.classList.remove("is-active", "is-done");
          if (p === curPhase) chip.classList.add("is-active");
          else if (p < curPhase) chip.classList.add("is-done");
        });
      });
    }

    function handleMultiToggle(screen, pill) {
      const screenId = screen.getAttribute("data-cx-screen");
      const val      = pill.getAttribute("data-cx-multi-val");
      if (!val) return;
      pill.classList.toggle("is-selected");
      const selected = Array.from(screen.querySelectorAll(".cx-pill--option.is-selected"))
        .map(p => p.getAttribute("data-cx-multi-val"));
      state.answers[screenId] = selected;
      const countEl = screen.querySelector("[data-cx-multi-count]");
      const ctaEl   = screen.querySelector("[data-cx-multi-go]");
      if (countEl) {
        countEl.textContent = selected.length === 0
          ? "Pick at least one"
          : (selected.length === 1 ? "1 selected · we hear you" : selected.length + " selected · we hear you");
      }
      if (ctaEl) {
        if (selected.length > 0) ctaEl.removeAttribute("disabled");
        else ctaEl.setAttribute("disabled", "");
      }
    }

    root.addEventListener("click", function (e) {
      const multiPill = e.target.closest("[data-cx-multi-val]");
      if (multiPill) {
        e.preventDefault();
        const screen = multiPill.closest("[data-cx-screen]");
        if (screen) handleMultiToggle(screen, multiPill);
        return;
      }

      const tap = e.target.closest("[data-cx-next]");
      if (tap) {
        e.preventDefault();
        const next = tap.getAttribute("data-cx-next");
        const screen = tap.closest("[data-cx-screen]");
        const screenId = screen ? screen.getAttribute("data-cx-screen") : null;
        const value = tap.getAttribute("data-cx-value");
        const isMultiGo = tap.hasAttribute("data-cx-multi-go");
        if (isMultiGo && tap.hasAttribute("disabled")) return;
        if (!isMultiGo && screenId && value) state.answers[screenId] = value;
        if (screenId && value) {
          track("egy_cx_quiz_step_answered", {
            step: screenId,
            answer: isMultiGo ? state.answers[screenId] : value,
            step_dwell_ms: Date.now() - (state.stepEnteredAt || Date.now())
          });
        } else if (isMultiGo && screenId) {
          track("egy_cx_quiz_step_answered", {
            step: screenId,
            answer: state.answers[screenId] || [],
            step_dwell_ms: Date.now() - (state.stepEnteredAt || Date.now())
          });
        }
        go(next);
        return;
      }

      const stepBtn = e.target.closest("[data-cx-step]");
      if (stepBtn) {
        e.preventDefault();
        const product = stepBtn.getAttribute("data-cx-product");
        const dir = stepBtn.getAttribute("data-cx-step");
        let qty = state.qty[product] || 0;
        if (dir === "+") qty = Math.min(qty + 1, 9);
        else qty = Math.max(qty - 1, 0);
        state.qty[product] = qty;
        const qtyEl = root.querySelector('[data-cx-qty="' + product + '"]');
        if (qtyEl) qtyEl.textContent = qty;
        updateReceipt();
        return;
      }

      const subBtn = e.target.closest("[data-cx-sub-toggle]");
      if (subBtn) {
        e.preventDefault();
        state.subscribe = !state.subscribe;
        subBtn.setAttribute("aria-pressed", state.subscribe ? "true" : "false");
        updateReceipt();
        return;
      }

      const addBtn = e.target.closest("[data-cx-add-to-cart]");
      if (addBtn) {
        e.preventDefault();
        addToCart();
        return;
      }
    });

    /* === q5 email submit (mid-funnel) — advances to q6 cost === */
    const emailForm = root.querySelector("[data-cx-email-form]");
    if (emailForm) {
      emailForm.addEventListener("submit", function (e) {
        e.preventDefault();
        const emailInput = emailForm.querySelector('input[type="email"]');
        const email = emailInput ? emailInput.value : "";
        state.answers.email = email;
        track("egy_cx_quiz_email_captured", {
          email: email,
          full_answers: state.answers,
          recommended_stack: Object.keys(state.qty).filter(k => state.qty[k] > 0).join("+")
        });
        go("q6");
      });
    }

    /* === Receipt math (unchanged) === */
    const PRICE_TABLE = {
      sleep:      { otp: 39.99, sub: 28.99 },
      essentials: { otp: 39.99, sub: 28.99 },
      energy:     { otp: 39.99, sub: 28.99 },
      beauty:     { otp: 36.50, sub: 28.99 }
    };
    const SUB_PCT_LABEL        = 25;
    const BUNDLE_2_PCT         = 10;
    const BUNDLE_3_PCT         = 20;
    const WELLNESS_BONUS_FULL  = 23;
    const WELLNESS_BONUS_THREE = 15;
    const CLEAN_ENERGY_BONUS   = 15;
    const TOTAL_PRODUCTS       = 4;
    const round = n => Math.round(n * 100) / 100;
    const fmt = n => "$" + n.toFixed(2);

    function updateReceipt() {
      const lineWrap = root.querySelector("[data-cx-receipt-lines]");
      if (!lineWrap) return;

      const productKeys = ["energy", "essentials", "sleep", "beauty"];
      const productLabels = {
        energy:     "Energy × 30ct",
        essentials: "Essentials × 30ct",
        sleep:      "Sleep × 30ct",
        beauty:     "Beauty × 30ct"
      };
      const items = productKeys.filter(k => state.qty[k] > 0);
      const uniqueCount = items.length;
      const totalQty = items.reduce((s, k) => s + state.qty[k], 0);

      while (lineWrap.firstChild) lineWrap.removeChild(lineWrap.firstChild);

      if (totalQty === 0) {
        setText(root, "[data-cx-subtotal]", "$0.00");
        setText(root, "[data-cx-total]", "$0.00");
        return;
      }

      const subscribe      = !!state.subscribe;
      const subtotalOtp    = round(items.reduce((s, k) => s + state.qty[k] * PRICE_TABLE[k].otp, 0));
      const afterSubAmount = round(items.reduce((s, k) => s + state.qty[k] * (subscribe ? PRICE_TABLE[k].sub : PRICE_TABLE[k].otp), 0));
      const subSavings     = subscribe ? round(subtotalOtp - afterSubAmount) : 0;
      const bundlePct      = totalQty >= 3 ? BUNDLE_3_PCT : (totalQty === 2 ? BUNDLE_2_PCT : 0);
      const bundleAmt      = round(items.reduce((s, k) => {
        const linePrice = subscribe ? PRICE_TABLE[k].sub : PRICE_TABLE[k].otp;
        const perLineDisc = Math.floor(linePrice * (bundlePct / 100) * 100) / 100;
        return s + perLineDisc * state.qty[k];
      }, 0));
      const afterBundle    = round(afterSubAmount - bundleAmt);
      const wellnessAmt    = uniqueCount === TOTAL_PRODUCTS       ? WELLNESS_BONUS_FULL
                            : uniqueCount === TOTAL_PRODUCTS - 1 ? WELLNESS_BONUS_THREE
                            : 0;
      const wellnessLabel  = uniqueCount === TOTAL_PRODUCTS       ? "− 🎁 Wellness Bonus (all 4 added)"
                            : uniqueCount === TOTAL_PRODUCTS - 1 ? "− 🎁 Wellness Bonus (3 added)"
                            : "− 🎁 Wellness Bonus";
      const cleanEnergyAmt = (subscribe
                              && state.cleanEnergyEligible
                              && (state.qty.energy || 0) >= 2
                              && subtotalOtp >= 150)
                              ? CLEAN_ENERGY_BONUS : 0;
      const finalTotal     = round(afterBundle - wellnessAmt - cleanEnergyAmt);
      const totalSavings   = round(subtotalOtp - finalTotal);

      items.forEach(k => {
        const qty = state.qty[k];
        const label = qty > 1 ? `${productLabels[k]} (×${qty})` : productLabels[k];
        const row = document.createElement("div");
        row.className = "cx-r__line";
        const labelSpan = document.createElement("span");
        labelSpan.textContent = label;
        const valueSpan = document.createElement("span");
        valueSpan.textContent = fmt(round(qty * PRICE_TABLE[k].otp));
        row.appendChild(labelSpan);
        row.appendChild(valueSpan);
        lineWrap.appendChild(row);
      });

      setText(root, "[data-cx-subtotal]",          fmt(subtotalOtp));
      setText(root, "[data-cx-bundle-label]",      bundlePct > 0 ? `− Bundle savings (${bundlePct}% off)` : "− Bundle savings");
      setText(root, "[data-cx-bundle-discount]",   bundleAmt    > 0 ? "−" + fmt(bundleAmt)   : "$0.00");
      setText(root, "[data-cx-sub-discount]",      subSavings   > 0 ? "−" + fmt(subSavings)  : "$0.00");
      setText(root, "[data-cx-wellness-label]",    wellnessLabel);
      setText(root, "[data-cx-wellness-discount]", wellnessAmt  > 0 ? "−" + fmt(wellnessAmt) : "$0.00");
      setText(root, "[data-cx-clean-discount]",    cleanEnergyAmt > 0 ? "−" + fmt(cleanEnergyAmt) : "$0.00");
      setText(root, "[data-cx-total]",             fmt(finalTotal) + (subscribe ? "/mo" : ""));
      setText(root, "[data-cx-strike]",            totalSavings > 0 ? fmt(subtotalOtp) : "");
      setText(root, "[data-cx-save]",              totalSavings > 0 ? `🎉 You save ${fmt(totalSavings)} ${subscribe ? "vs. one-time" : "today"}` : "");
      setText(root, "[data-cx-cta-total]",         `Complete Checkout  →`);

      const bundleRow   = root.querySelector("[data-cx-bundle-row]");
      if (bundleRow)   bundleRow.style.display   = bundleAmt > 0 ? "" : "none";
      const wellnessRow = root.querySelector("[data-cx-wellness-row]");
      if (wellnessRow) wellnessRow.style.display = wellnessAmt > 0 ? "" : "none";
      const cleanRow    = root.querySelector("[data-cx-clean-row]");
      if (cleanRow)    cleanRow.style.display    = cleanEnergyAmt > 0 ? "" : "none";
      const subRow      = root.querySelector("[data-cx-sub-row]");
      if (subRow)      subRow.style.display      = subSavings > 0 ? "" : "none";

      productKeys.forEach(p => {
        const tile = root.querySelector('[data-cx-tile][data-product="' + p + '"]');
        if (tile) tile.setAttribute("data-qty", String(state.qty[p] || 0));
      });
    }

    function setText(scope, sel, value) {
      const el = scope.querySelector(sel);
      if (el) el.textContent = value;
    }

    function computeCartDiscountCodes() {
      const codes = [];
      const items = Object.keys(state.qty).filter(k => state.qty[k] > 0);
      const uniqueCount = items.length;
      const totalQty = items.reduce((s, k) => s + state.qty[k], 0);
      const subtotalOtp = items.reduce((s, k) => s + state.qty[k] * PRICE_TABLE[k].otp, 0);
      if (totalQty >= 3) codes.push(DISCOUNT_CODES.bundle_3);
      else if (totalQty === 2) codes.push(DISCOUNT_CODES.bundle_2);
      if (uniqueCount === 4) codes.push(DISCOUNT_CODES.wellness_full);
      else if (uniqueCount === 3) codes.push(DISCOUNT_CODES.wellness_three);
      if (state.subscribe
          && state.cleanEnergyEligible
          && (state.qty.energy || 0) >= 2
          && subtotalOtp >= 150) {
        codes.push(DISCOUNT_CODES.clean_energy);
      }
      return codes;
    }

    function buildCartUrl(cfg, items, subscribe, codes) {
      const lines = items.map(it => {
        const vid = cfg.variants[it.product];
        return vid ? (vid + ":" + it.qty) : null;
      }).filter(Boolean);
      if (!lines.length) return null;
      const params = [];
      if (subscribe && cfg.monthly_selling_plan) params.push("selling_plan=" + encodeURIComponent(cfg.monthly_selling_plan));
      if (codes.length) params.push("discount=" + encodeURIComponent(codes.join(",")));
      const qs = params.length ? "?" + params.join("&") : "";
      return "https://" + cfg.shop_domain + "/cart/" + lines.join(",") + qs;
    }

    function isOnShopify() {
      const h = location.hostname || "";
      return h.endsWith(".myshopify.com") || h === "nustrips.com" || h.endsWith(".nustrips.com");
    }

    function suppressSlideCart() {
      const close = () => {
        try {
          if (typeof window.upcartCloseCart === "function") window.upcartCloseCart();
          document.body.classList.remove(
            "upcartPopupShow", "upcart-show", "cart-drawer-open",
            "drawer-open", "mini-cart-open", "js-drawer-open"
          );
        } catch (e) {}
      };
      close();
      const id = setInterval(close, 150);
      setTimeout(() => clearInterval(id), 5000);
    }

    async function warmCart(cfg, items, subscribe, codes) {
      if (!isOnShopify()) return;
      const planId = cfg.monthly_selling_plan ? parseInt(cfg.monthly_selling_plan, 10) : null;
      const adds = items.map(it => {
        const vid = parseInt(cfg.variants[it.product], 10);
        if (!vid) return null;
        const line = { id: vid, quantity: it.qty };
        if (subscribe && planId) line.selling_plan = planId;
        return line;
      }).filter(Boolean);
      if (!adds.length) return;
      try { await fetch("/cart/clear.js", { method: "POST", headers: { "Accept": "application/json" } }); } catch (e) { return; }
      suppressSlideCart();
      try {
        await fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", "Accept": "application/json" },
          body: JSON.stringify({ items: adds })
        });
      } catch (e) { return; }
      suppressSlideCart();
      for (let i = 0; i < codes.length; i++) {
        if (i > 0) await new Promise(r => setTimeout(r, 800));
        try { await fetch("/discount/" + encodeURIComponent(codes[i]) + "?redirect=/cart.js", { redirect: "follow" }); } catch (e) {}
        suppressSlideCart();
      }
    }

    async function submitCartFormPost(cfg, items, subscribe, codes) {
      const planId = cfg.monthly_selling_plan ? parseInt(cfg.monthly_selling_plan, 10) : null;
      const adds = items.map(it => {
        const vid = parseInt(cfg.variants[it.product], 10);
        if (!vid) return null;
        const line = { id: vid, quantity: it.qty };
        if (subscribe && planId) line.selling_plan = planId;
        return line;
      }).filter(Boolean);
      if (!adds.length) return;
      try { await fetch("/cart/clear.js", { method: "POST", headers: { "Accept": "application/json" } }); } catch (e) {}

      const form = document.createElement("form");
      form.method = "post";
      form.action = "/cart/add";
      form.style.display = "none";

      adds.forEach((item, i) => {
        Object.entries(item).forEach(([k, v]) => {
          if (v === undefined || v === null) return;
          const inp = document.createElement("input");
          inp.type = "hidden";
          inp.name = "items[" + i + "][" + k + "]";
          inp.value = String(v);
          form.appendChild(inp);
        });
      });

      const trackingParams = new URLSearchParams();
      const passthrough = /^(utm_|gclid$|fbclid$|ttclid$|msclkid$|li_fat_id$|irclickid$|ref$)/i;
      new URLSearchParams(window.location.search).forEach((v, k) => {
        if (passthrough.test(k)) trackingParams.set(k, v);
      });
      if (codes.length) trackingParams.set("discount", codes.join(","));
      const qs = trackingParams.toString();

      const returnTo = document.createElement("input");
      returnTo.type = "hidden";
      returnTo.name = "return_to";
      returnTo.value = "/checkout" + (qs ? ("?" + qs) : "");
      form.appendChild(returnTo);

      document.body.appendChild(form);
      form.submit();
    }

    function addToCart() {
      const items = [];
      Object.keys(state.qty).forEach(p => {
        if (state.qty[p] > 0) items.push({ product: p, qty: state.qty[p] });
      });
      const totalEl = root.querySelector("[data-cx-total]");
      const savedEl = root.querySelector("[data-cx-save]");
      const parseDollar = el => el ? parseFloat((el.textContent || "0").replace(/[^0-9.]/g, "")) : 0;
      const cfg = (window.CX_QUIZ_CONFIG && window.CX_QUIZ_CONFIG.variants) ? window.CX_QUIZ_CONFIG : PRODUCT_CONFIG_DEFAULT;
      const codes = computeCartDiscountCodes();
      const onShopify = isOnShopify();
      const cartUrl = buildCartUrl(cfg, items, !!state.subscribe, codes);

      track("egy_cx_quiz_cart_added", {
        cart_payload: items,
        total: parseDollar(totalEl),
        savings: parseDollar(savedEl),
        codes: codes,
        subscribe: !!state.subscribe,
        cart_url: cartUrl,
        submit_mode: onShopify ? "form_post" : "permalink"
      });

      window.dispatchEvent(new CustomEvent("egy-cx-quiz:add-to-cart", {
        detail: { items, answers: state.answers, codes: codes, subscribe: !!state.subscribe, cart_url: cartUrl }
      }));

      if (onShopify) {
        setTimeout(() => { submitCartFormPost(cfg, items, !!state.subscribe, codes); }, 50);
      } else if (cartUrl) {
        setTimeout(() => { window.location.assign(cartUrl); }, 50);
      }
    }

    function lockApprovedPct() {
      const recItems = Object.keys(state.qty).filter(k => state.qty[k] > 0);
      const recUnique = recItems.length;
      const recTotalQty = recItems.reduce((s, k) => s + state.qty[k], 0);
      if (recTotalQty === 0) return;
      const recSubtotalOtp = round(recItems.reduce((s, k) => s + state.qty[k] * PRICE_TABLE[k].otp, 0));
      const recAfterSub    = round(recItems.reduce((s, k) => s + state.qty[k] * PRICE_TABLE[k].sub, 0));
      const recBundlePct   = recTotalQty >= 3 ? BUNDLE_3_PCT : (recTotalQty === 2 ? BUNDLE_2_PCT : 0);
      const recBundleAmt   = round(recAfterSub * recBundlePct / 100);
      const recWellness    = recUnique === TOTAL_PRODUCTS       ? WELLNESS_BONUS_FULL
                            : recUnique === TOTAL_PRODUCTS - 1 ? WELLNESS_BONUS_THREE
                            : 0;
      const recCleanEnergy = (state.cleanEnergyEligible && (state.qty.energy || 0) >= 2 && recSubtotalOtp >= 150)
                            ? CLEAN_ENERGY_BONUS : 0;
      const recFinal       = round(recAfterSub - recBundleAmt - recWellness - recCleanEnergy);
      const recPct         = Math.round(((recSubtotalOtp - recFinal) / recSubtotalOtp) * 100);
      root.querySelectorAll("[data-cx-approved-pct]").forEach(el => {
        el.textContent = String(recPct);
      });
    }

    screens.forEach((s, i) => { if (i === 0) s.classList.add("is-active"); });
    updateProgress();
    updatePhaseRow();
    updateReceipt();
    lockApprovedPct();
  }

  document.addEventListener("DOMContentLoaded", function () {
    document.querySelectorAll("[data-cx-quiz-root]").forEach(init);
  });

  window.EGYCXQuiz = { init };
})();
